import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { activeClientState, dataVersionState } from '../../state.js';
import { useSwitchClient } from '../ClientPicker.js';
import { useApi } from '../../hooks.js';
import { Card } from '../Card.js';
import { ConfirmDialog } from '../ConfirmDialog.js';
import { ErrorBanner } from '../FormBits.js';
import { Table, THeadRow, TH, TD, TR, Ltr } from '../ui/table.js';
import { fmtNum } from '../../utils.js';
import { PdfReview } from './PdfReview.js';
import { FlowSteps } from './FlowSteps.js';
import {
  pdfBatch, type Batch, type BatchProgress, type FileEdits, type OpenBatch, type PublishResult,
} from './pdfBatchApi.js';
import type { ClientInfo } from '@agio/core';
import { detectFileKind, FILE_KIND_HEAD_BYTES, renderMessage, type UploadMessage } from '@agio/core/domain';
import { ApiError } from '../../api.js';

/** The upload of one file's bytes — seconds. The slow part is the reading. */
type QueueStatus = 'pending' | 'uploading' | 'uploaded' | 'error';

interface QueuedFile {
  /** name+size+lastModified — stable enough to spot the same file picked twice. */
  key: string;
  file: File;
  status: QueueStatus;
  error?: string;
  /** Why the file was refused, explained — shown on its row, not hidden in a tooltip. */
  message?: UploadMessage;
}

/**
 * pick → uploading → reading → review → published.
 *
 * The stage that matters is `review`. Reading writes nothing a client can see; a
 * person looks at what the model read, fixes the fund name, answers the
 * deposits/withdrawals question, and only then publishes. Everything published
 * carries its batch id, so a bad month can be undone instead of repaired by
 * hand in the database.
 *
 * `uploading` and `reading` are one step on screen: the files go up in seconds,
 * and the 3–5 minutes per file after that is what anyone is actually waiting on.
 */
type Stage = 'pick' | 'uploading' | 'reading' | 'review' | 'published';

const keyOf = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;

const fmtSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Hebrew counts one file differently from many: "קובץ אחד", not "1 קבצים". */
const files = (n: number) => (n === 1 ? 'קובץ אחד' : `${n} קבצים`);

const STEPS = [
  { id: 'pick', label: 'בחירת קבצים' },
  { id: 'extracting', label: 'קריאה' },
  { id: 'review', label: 'בדיקה' },
  { id: 'published', label: 'פרסום' },
];

const STATUS_LABEL: Record<QueueStatus, string> = {
  pending: 'ממתין להעלאה',
  uploading: 'מעלה…',
  uploaded: 'הועלה',
  error: 'שגיאה',
};

const sleep = (ms: number) => new Promise((done) => setTimeout(done, ms));

export function MonthlyPdfCard() {
  const bump = useSetRecoilState(dataVersionState);
  const { data: clients } = useApi<ClientInfo[]>('/clients');
  // The client picked in the header. The upload is always for that client.
  const clientId = useRecoilValue(activeClientState) ?? '';
  const switchClient = useSwitchClient();
  const [stage, setStage] = useState<Stage>('pick');
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  const [batch, setBatch] = useState<Batch | null>(null);
  const [result, setResult] = useState<PublishResult | null>(null);
  const [confirming, setConfirming] = useState<'extract' | 'publish' | 'discard' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  // Uploads that were read but never published. Nothing is written until פרסום
  // and קליטות אחרונות only lists what was published, so walking away from a
  // review used to erase every sign that the upload had ever happened.
  const [drafts, setDrafts] = useState<OpenBatch[]>([]);
  const [draftsTick, setDraftsTick] = useState(0);
  const [doomedDraft, setDoomedDraft] = useState<OpenBatch | null>(null);
  // How far the reading has got, straight from the server — so the counter is
  // the same in every window and a reload continues it instead of restarting.
  const [progress, setProgress] = useState<BatchProgress | null>(null);
  // The batch this window is driving. A reload starts a new window, which picks
  // the same batch up; the ref keeps the old window's loop from fighting it.
  const driving = useRef<string | null>(null);
  // Bumped by every reset. An upload that started before one must not carry on
  // into reading and review for a client the header has since moved away from.
  const run = useRef(0);

  useEffect(() => {
    if (stage !== 'pick') return;
    let live = true;
    pdfBatch.listOpen()
      .then((d) => live && setDrafts(d))
      .catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [stage, draftsTick]);

  /**
   * Read whatever is still unread, one file per request, then go to review.
   *
   * Any window can drive this, because the queue is on the server: the files are
   * already uploaded, and each request just asks for the next one. That is what
   * makes a reload harmless — the new page joins the same reading instead of
   * starting a second one.
   */
  const readAll = async (batchId: string) => {
    driving.current = batchId;
    setStage('reading');
    for (;;) {
      let step;
      try {
        step = await pdfBatch.extractNext(batchId);
      } catch (err) {
        // Connection lost mid-read. Whatever was read is safe on the server, so
        // the honest thing is to stop and offer to carry on.
        setError((err as Error).message);
        return;
      }
      if (driving.current !== batchId) return; // a reset or another batch took over
      setProgress(step);
      if (step.pending === 0 && step.extracting === 0) break;
      // Nothing was free to read: another window is on the last file. Wait for it.
      if (step.read === null) await sleep(3000);
    }
    try {
      setBatch(await pdfBatch.get(batchId));
      setStage('review');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Reading a month takes minutes, and a review can then sit unpublished for a
  // while longer. Losing either to a stray refresh would be its own reason not to
  // trust the flow — so the batch id goes into the URL the moment the upload
  // starts, and a reload picks up wherever it had got to: back into the same
  // counter if files are still being read, or into the review if they are done.
  const resumeId = params.get('batch');
  useEffect(() => {
    if (!resumeId || resumeId === driving.current || batch?.batch_id === resumeId) return;
    let live = true;
    pdfBatch.get(resumeId)
      .then((b) => {
        if (!live) return;
        setBatch(b);
        // A link to an upload opens it for ITS client, whoever the header was on:
        // the header follows the batch, so the whole admin area agrees on it.
        switchClient(b.client_id);
        if (b.status !== 'draft') {
          setStage('pick');
          return;
        }
        const unread = b.files.filter((f) => f.status === 'pending' || f.status === 'extracting').length;
        if (unread > 0) void readAll(resumeId);
        else setStage('review');
      })
      .catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [resumeId, batch?.batch_id]);

  // While a file is being read the driving request is in flight for minutes, so
  // nothing would refresh the counter in the meantime. Polling keeps the clock and
  // the file name live — and identical in every window looking at this upload.
  // A failed poll is left silent on purpose: it is a blip in a display, and the
  // reading loop itself reports anything that actually went wrong.
  useEffect(() => {
    if (stage !== 'reading' || !resumeId) return;
    let live = true;
    const tick = () => pdfBatch.progress(resumeId)
      .then((p) => { if (live) setProgress(p); })
      .catch(() => {});
    void tick();
    const timer = setInterval(() => void tick(), 5000);
    return () => { live = false; clearInterval(timer); };
  }, [stage, resumeId]);

  // Switching client in the header leaves whatever was in progress. Nothing is
  // lost: an upload already started is an open draft on the server, listed here
  // again when that client is picked. Files only picked, not uploaded, are
  // dropped — they were chosen for the other client.
  const shownFor = useRef(clientId);
  useEffect(() => {
    if (shownFor.current === clientId) return;
    shownFor.current = clientId;
    if (batch?.client_id === clientId) return; // the header just followed an opened link
    if (batch || driving.current || stage !== 'pick') reset();
    else { setQueue([]); setError(null); }
  }, [clientId, batch]);

  const clientNameOf = (id?: string) =>
    clients?.find((c) => c.client_id === id)?.display_name_he ?? id ?? '';
  // The batch's own client when there is one — the thing being published.
  const clientName = clientNameOf(batch?.client_id ?? clientId);
  const uploaded = queue.filter((q) => q.status === 'uploaded' || q.status === 'error').length;
  /** One upload per client at a time — the server refuses a second one too. */
  const openForClient = drafts.find((d) => d.client_id === clientId);
  const myDrafts = drafts.filter((d) => d.client_id === clientId);

  /** Adding is cumulative: pick a few, then a few more, without losing the first. */
  const addFiles = (picked: FileList | null) => {
    if (!picked?.length) return;
    // Snapshot NOW. A FileList is a live view of the input, and the caller clears
    // the input straight after — read it inside the state updater instead and it
    // is sometimes already empty by the time React runs it, so the second batch
    // of files silently vanished.
    const chosen = Array.from(picked);
    setError(null);
    // What each file really is, from its first bytes — the picker's ".pdf"
    // filter is skipped by drag-and-drop and by "all files". A non-PDF is kept
    // in the list, refused on the spot and explained, and never uploaded.
    void Promise.all(
      chosen.map(async (f): Promise<QueuedFile> => {
        const found = detectFileKind(new Uint8Array(await f.slice(0, FILE_KIND_HEAD_BYTES).arrayBuffer()));
        if (found === 'pdf') return { key: keyOf(f), file: f, status: 'pending' };
        const message = renderMessage({ kind: 'wrong_file_type', screen: 'monthly_pdf', filename: f.name, found });
        return { key: keyOf(f), file: f, status: 'error', error: message.title, message };
      }),
    ).then((checked) =>
      setQueue((current) => {
        const seen = new Set(current.map((q) => q.key));
        return [...current, ...checked.filter((f) => !seen.has(f.key))];
      }),
    );
  };

  const reset = () => {
    driving.current = null;
    run.current += 1;
    setStage('pick');
    setQueue([]);
    setProgress(null);
    setBatch(null);
    setResult(null);
    setError(null);
    setParams((p) => { p.delete('batch'); return p; }, { replace: true });
    setDraftsTick((v) => v + 1);
  };

  /**
   * Phase 1: upload the month, then read it. Nothing client-facing is written.
   *
   * The upload goes first and takes seconds, so within a moment of pressing the
   * button the files are safe on the server and the batch id is in the URL. Only
   * then does the slow reading start — which is why a reload during it costs
   * nothing but the wait.
   */
  const extract = async () => {
    setConfirming(null);
    setError(null);
    setProgress(null);
    setStage('uploading');
    const mine = run.current;
    let batchId: string;
    try {
      batchId = await pdfBatch.create(clientId);
    } catch (err) {
      setError((err as Error).message);
      setStage('pick');
      return;
    }
    if (run.current !== mine) return; // left before it started — an empty draft, listed under its client
    driving.current = batchId;
    // In the URL before a single byte moves: from here on, a reload rejoins this
    // upload instead of losing it.
    setParams((p) => { p.set('batch', batchId); return p; }, { replace: true });

    for (const item of queue) {
      if (item.status === 'error') continue; // refused when picked — never sent
      setQueue((q) => q.map((f) => (f.key === item.key ? { ...f, status: 'uploading' } : f)));
      try {
        setProgress(await pdfBatch.addFile(batchId, item.file));
        setQueue((q) => q.map((f) => (f.key === item.key ? { ...f, status: 'uploaded' } : f)));
      } catch (err) {
        // One bad file must not abandon the rest of the month's upload.
        const message = err instanceof ApiError ? err.messages?.[0] : undefined;
        setQueue((q) =>
          q.map((f) => (f.key === item.key ? { ...f, status: 'error', error: (err as Error).message, message } : f)),
        );
      }
    }
    // The files finish uploading even if the header moved on — they are safe on
    // the server — but the reading waits for someone to open that draft again.
    if (run.current !== mine) return;
    await readAll(batchId);
  };

  /** Pick a review back up. The `?batch=` effect above does the loading. */
  const resume = (batchId: string) => {
    setError(null);
    setParams((p) => { p.set('batch', batchId); return p; }, { replace: true });
  };

  const discardDraft = async () => {
    if (!doomedDraft) return;
    setBusy(true);
    setError(null);
    try {
      await pdfBatch.discard(doomedDraft.batch_id);
      setDraftsTick((v) => v + 1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      setDoomedDraft(null);
    }
  };

  const withBatch = async (fn: () => Promise<Batch>) => {
    setBusy(true);
    setError(null);
    try {
      setBatch(await fn());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const editFile = (fileId: string, edits: FileEdits) =>
    void withBatch(() => pdfBatch.edit(batch!.batch_id, fileId, edits));

  const removeFile = (fileId: string) =>
    void withBatch(() => pdfBatch.removeFile(batch!.batch_id, fileId));

  const publish = async () => {
    setConfirming(null);
    setBusy(true);
    setError(null);
    try {
      setResult(await pdfBatch.publish(batch!.batch_id));
      setStage('published');
      bump((v) => v + 1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setConfirming(null);
    setBusy(true);
    try {
      await pdfBatch.discard(batch!.batch_id);
      reset();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const extracted = batch?.files.filter((f) => f.status === 'extracted').length ?? 0;
  // Publishing without the month's deposits/withdrawals is allowed, and updates
  // the balance only. That is right — but it was one small note on the card, and
  // a client published straight past it and then asked why only the value moved.
  // So the confirmation says it, by name, at the moment of deciding.
  const noReturn = batch?.files.filter((f) => f.status === 'extracted' && !f.flows_confirmed) ?? [];

  return (
    <Card title="עדכון חודשי — סטייטמנט PDF לכל תיק">
      <ConfirmDialog
        open={confirming === 'extract'}
        title="לקרוא את הקבצים?"
        confirmLabel={`קרא ${files(queue.length)}`}
        onConfirm={() => void extract()}
        onCancel={() => setConfirming(null)}
      >
        <p>
          {files(queue.length)} {queue.length === 1 ? 'ייקרא' : 'ייקראו'} עבור{' '}
          <span className="font-semibold">{clientName}</span>.
        </p>
        <p className="mt-2 text-muted-foreground">
          הקבצים יועלו מיד, ואז ייקראו אחד אחרי השני — כ-3–4 דקות לקובץ. אפשר לרענן או לסגור את
          הדף בזמן הקריאה; היא תימשך, והחזרה לכאן תציג את אותו מצב. בשלב הזה עוד לא נשמר דבר —
          אחרי הקריאה תוכלו לראות מה חולץ, לתקן, ורק אז לפרסם.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirming === 'publish'}
        title="לפרסם ללקוח?"
        confirmLabel={`פרסם ${files(extracted)}`}
        onConfirm={() => void publish()}
        onCancel={() => setConfirming(null)}
      >
        <p>
          הנתונים של <span className="font-semibold">{clientName}</span> יתעדכנו בפורטל.
        </p>
        {noReturn.length > 0 && (
          <div className="mt-3 rounded-md border border-border bg-muted/40 p-2.5 text-sm">
            <p className="font-semibold text-warning">
              בלי תשואה: {noReturn.map((f) => f.fund).join(', ')}
            </p>
            <p className="mt-1">
              לא אושרו הפקדות ומשיכות, ולכן תתעדכן רק היתרה — התשואה ומדד הייחוס של החודש לא יחושבו.
              כדי שיחושבו, בטלו כאן ואשרו את התנועות (או "לא היו תנועות").
            </p>
          </div>
        )}
        <p className="mt-2 text-muted-foreground">
          אפשר לבטל את הפרסום אחר כך דרך "קליטות אחרונות" — הביטול מחזיר את המצב הקודם.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirming === 'discard'}
        title="למחוק את הקריאה?"
        confirmLabel="מחק"
        onConfirm={() => void discard()}
        onCancel={() => setConfirming(null)}
      >
        <p>מה שחולץ יימחק ולא יפורסם. הקבצים עצמם לא נפגעים.</p>
      </ConfirmDialog>

      {/* Discarding a review throws its documents away, so it is asked about
          rather than done on one click — the same courtesy the review screen's
          own discard already had. */}
      <ConfirmDialog
        open={!!doomedDraft}
        title="למחוק את הקליטה הזו?"
        confirmLabel="מחק"
        onConfirm={() => void discardDraft()}
        onCancel={() => setDoomedDraft(null)}
      >
        <p>
          מה שנקרא עבור <span className="font-semibold">{clientNameOf(doomedDraft?.client_id)}</span>{' '}
          יימחק ולא יפורסם. הקבצים במחשב שלכם לא נפגעים.
        </p>
        {!!doomedDraft?.extracting && (
          <p className="mt-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-warning">
            בקליטה הזו יש {files(doomedDraft.extracting)} שהקריאה שלהם לא הסתיימה. אם מישהו קורא
            אותם עכשיו בחלון אחר, המחיקה תבטל את העבודה הזו.
          </p>
        )}
      </ConfirmDialog>

      <div className="grid gap-4">
        <FlowSteps steps={STEPS} current={stage === 'uploading' || stage === 'reading' ? 'extracting' : stage} />

        {/* The form stays narrow; the review table below needs the full width. */}
        {stage === 'pick' && myDrafts.length > 0 && (
          <OpenDrafts
            drafts={myDrafts}
            busy={busy}
            onResume={resume}
            onDiscard={(d) => setDoomedDraft(d)}
          />
        )}

        {stage === 'pick' && (
          <div className="grid max-w-3xl gap-4">
            <label className="text-sm">
              <span className="mb-1 block text-muted-foreground">קבצי PDF של החודש</span>
              <input
                type="file"
                accept=".pdf"
                multiple
                className="w-full rounded-md border border-input bg-popover px-3 py-2 file:me-3 file:rounded file:border-0 file:bg-muted file:px-3 file:py-1 file:text-foreground"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = ''; // so picking the same file again still registers
                }}
              />
            </label>
          </div>
        )}

        {stage !== 'review' && stage !== 'published' && queue.length > 0 && (
          <PickedFiles
            queue={queue}
            running={stage !== 'pick'}
            onRemove={(key) => setQueue((q) => q.filter((f) => f.key !== key))}
          />
        )}

        {stage === 'uploading' && (
          <p className="text-sm text-muted-foreground">
            מעלה את הקבצים לשרת — {uploaded} מתוך {queue.length}. מיד לאחר מכן מתחילה הקריאה.
          </p>
        )}

        {stage === 'reading' && (
          <ReadProgress
            progress={progress}
            stalled={!!error}
            onContinue={() => { setError(null); void readAll(resumeId!); }}
          />
        )}

        {stage === 'review' && batch && (
          <>
            <ReviewHeader clientName={clientName} count={extracted} />
            <PdfReview batch={batch} busy={busy} onEdit={editFile} onRemove={removeFile} />
          </>
        )}

        {stage === 'published' && result && <PublishedSummary result={result} onAgain={reset} />}

        <div className="flex flex-wrap items-center gap-3">
          {stage === 'pick' && (
            <>
              <button
                type="button"
                disabled={!queue.some((f) => f.status === 'pending') || !clientId || !!openForClient}
                onClick={() => setConfirming('extract')}
                className="rounded-md bg-primary px-6 py-2 font-semibold text-primary-foreground disabled:opacity-50"
              >
                קרא קבצים
              </button>
              {!clientId && (
                <span className="text-xs text-muted-foreground">בחרו לקוח למעלה כדי להתחיל.</span>
              )}
              {/* One upload per client at a time. Two of them running would each
                  write the same months, and whichever was published last would
                  win — so the open one is finished or thrown away first. */}
              {openForClient && (
                <span className="text-xs text-warning">
                  ל{clientName} יש קליטה פתוחה שלא פורסמה — המשיכו אותה או מחקו אותה למעלה כדי להתחיל קליטה חדשה.
                </span>
              )}
            </>
          )}

          {stage === 'review' && batch && (
            <>
              <button
                type="button"
                disabled={!batch.can_publish || busy}
                onClick={() => setConfirming('publish')}
                className="rounded-md bg-primary px-6 py-2 font-semibold text-primary-foreground disabled:opacity-50"
              >
                פרסם ללקוח
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirming('discard')}
                className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
              >
                מחק ואל תפרסם
              </button>
              {!batch.can_publish && (
                <span className="text-xs text-destructive">יש לתקן את המסומן באדום לפני פרסום.</span>
              )}
            </>
          )}
        </div>
      </div>

      <ErrorBanner error={error} />
    </Card>
  );
}

/**
 * Reviews that were started and never finished. Without this they exist only as
 * a `?batch=` URL someone still has open — invisible from every screen, and
 * indistinguishable from an upload that never happened.
 */
function OpenDrafts({
  drafts,
  busy,
  onResume,
  onDiscard,
}: {
  drafts: OpenBatch[];
  busy: boolean;
  onResume: (batchId: string) => void;
  onDiscard: (draft: OpenBatch) => void;
}) {
  return (
    <div className="rounded-md border border-warning/40 bg-warning/10 p-4">
      <p className="text-sm font-semibold text-warning">קליטות פתוחות שלא פורסמו</p>
      <p className="mt-1 text-xs text-muted-foreground">
        הקבצים האלה הועלו, אבל אף אחד לא לחץ "פרסם ללקוח" — ולכן שום דבר מהם לא הגיע לפורטל.
        המשיכו מכאן (גם אם הקריאה נקטעה באמצע — היא תימשך), או מחקו אם אינן נחוצות.
        לכל לקוח יכולה להיות קליטה פתוחה אחת בכל רגע. מוצגות רק הקליטות של הלקוח שנבחר למעלה.
      </p>
      <Table minWidth="28rem">
        <THeadRow>
          <TH>נקרא בתאריך</TH>
          <TH align="end">קבצים</TH>
          <TH align="end" />
        </THeadRow>
        <tbody>
          {drafts.map((d) => (
            <TR key={d.batch_id}>
              <TD className="text-muted-foreground"><Ltr>{d.created_at.slice(0, 16).replace('T', ' ')}</Ltr></TD>
              {/* "1 / 2" reads backwards in an RTL row — spell it out instead. */}
              <TD align="end">
                {d.extracted === d.files ? files(d.files) : `${d.extracted} מתוך ${d.files}`}
              </TD>
              <TD align="end">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onResume(d.batch_id)}
                    className="rounded-md bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground disabled:opacity-50"
                  >
                    {d.extracting > 0 ? 'המשך קריאה' : 'המשך בדיקה'}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onDiscard(d)}
                    className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50"
                  >
                    מחיקה
                  </button>
                </div>
              </TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function ReviewHeader({ clientName, count }: { clientName: string; count: number }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm">
      <p className="font-semibold">בדקו לפני פרסום</p>
      <p className="mt-1 text-muted-foreground">
        {files(count)} {count === 1 ? 'נקרא' : 'נקראו'} עבור {clientName}.{' '}
        <strong>שום דבר עדיין לא הגיע ללקוח.</strong> בדקו לאיזה תיק כל קובץ שייך, מלאו הפקדות
        ומשיכות, והשוו מול הקובץ עצמו. רק "פרסם ללקוח" מעדכן את הפורטל.
      </p>
    </div>
  );
}

function PublishedSummary({ result, onAgain }: { result: PublishResult; onAgain: () => void }) {
  const withReturn = result.published.filter((p) => p.return_computed).length;
  return (
    <div className="grid gap-3">
      <div className="rounded-md border border-success/40 bg-success/10 px-4 py-3 text-sm text-success">
        פורסם — {files(result.published.length)} {result.published.length === 1 ? 'עודכן' : 'עודכנו'} בפורטל.
        {withReturn < result.published.length && (
          <span className="mt-1 block text-muted-foreground">
            {result.published.length - withReturn} מתוכן ללא הפקדות/משיכות — היתרה עודכנה, אך לא תחושב להן תשואה החודש.
          </span>
        )}
      </div>
      <Table minWidth="30rem">
        <THeadRow>
          <TH>קרן</TH>
          <TH>תאריך דוח</TH>
          <TH align="end">סה"כ</TH>
          <TH align="end">ניירות</TH>
          <TH>תשואה</TH>
        </THeadRow>
        <tbody>
          {result.published.map((p) => (
            <TR key={p.fund}>
              <TD>{p.fund}</TD>
              <TD><Ltr>{p.report_date}</Ltr></TD>
              <TD align="end" className="tabular-nums">{fmtNum(p.total_value)}</TD>
              <TD align="end" className="tabular-nums text-muted-foreground">{p.positions}</TD>
              <TD className={p.return_computed ? 'text-success' : 'text-muted-foreground'}>
                {p.return_computed ? 'תחושב' : 'לא תחושב'}
              </TD>
            </TR>
          ))}
        </tbody>
      </Table>
      <div>
        <button type="button" onClick={onAgain} className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted">
          קליטה נוספת
        </button>
      </div>
    </div>
  );
}

/** The picked files, before and during extraction. */
function PickedFiles({
  queue,
  running,
  onRemove,
}: {
  queue: QueuedFile[];
  running: boolean;
  onRemove: (key: string) => void;
}) {
  return (
    <Table minWidth="30rem">
      <THeadRow>
        <TH>קובץ</TH>
        <TH>גודל</TH>
        <TH>סטטוס</TH>
        <TH align="end" />
      </THeadRow>
      <tbody>
        {queue.map((f) => (
          <TR key={f.key}>
            <TD>
              {f.file.name}
              {f.message && (
                <div className="mt-1 max-w-xl text-xs leading-relaxed text-amber-900">
                  <p className="font-semibold">{f.message.title}</p>
                  <p>{f.message.steps[0]}</p>
                </div>
              )}
            </TD>
            <TD className="text-muted-foreground"><Ltr>{fmtSize(f.file.size)}</Ltr></TD>
            <TD><StatusPill status={f.status} error={f.error} /></TD>
            <TD align="end">
              {/* Removing mid-run would leave a half-finished extraction behind. */}
              {!running && (f.status === 'pending' || (f.status === 'error' && !!f.message)) && (
                <button
                  type="button"
                  aria-label={`הסרת ${f.file.name}`}
                  onClick={() => onRemove(f.key)}
                  className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted"
                >
                  הסרה
                </button>
              )}
            </TD>
          </TR>
        ))}
      </tbody>
    </Table>
  );
}

function StatusPill({ status, error }: { status: QueueStatus; error?: string }) {
  if (status === 'uploaded') return <span className="text-success">{STATUS_LABEL.uploaded}</span>;
  if (status === 'error') {
    return <span className="text-destructive" title={error}>{STATUS_LABEL.error}</span>;
  }
  if (status === 'uploading') {
    return (
      <span className="flex items-center gap-1.5 text-foreground">
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
        {STATUS_LABEL.uploading}
      </span>
    );
  }
  return <span className="text-muted-foreground">{STATUS_LABEL.pending}</span>;
}

/**
 * The counter for the slow part: which file, how far in, how long it has been.
 *
 * Every number here comes from the server — the count of files read AND the
 * seconds the current one has been going. That is the point: reading a month
 * takes 3–5 minutes per file, and a browser-only counter vanished on refresh,
 * so the upload looked like it had never happened. Now any window shows the same
 * figures, and a reload carries the clock on rather than restarting it at zero.
 */
function ReadProgress({
  progress,
  stalled,
  onContinue,
}: {
  progress: BatchProgress | null;
  /** The reading stopped on an error — what was read is still safe. */
  stalled: boolean;
  onContinue: () => void;
}) {
  // The server's elapsed count, carried forward locally between polls so the
  // clock moves every second instead of jumping every five.
  const [now, setNow] = useState(() => Date.now());
  const since = progress?.reading?.elapsed_seconds;
  const [baseline, setBaseline] = useState(() => Date.now());
  useEffect(() => { setBaseline(Date.now()); setNow(Date.now()); }, [since, progress?.reading?.filename]);
  useEffect(() => {
    if (since === undefined) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [since]);

  const elapsed = since === undefined ? 0 : since + Math.max(0, Math.floor((now - baseline) / 1000));
  const clock = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;

  const total = progress?.total ?? 0;
  const done = (progress?.extracted ?? 0) + (progress?.failed ?? 0);
  const pct = total ? Math.round((done / total) * 100) : 0;
  const reading = progress?.reading ?? null;

  return (
    <div>
      <div className="mb-1 flex flex-wrap justify-between gap-3 text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'מתחיל…' : <>קורא קובץ {Math.min(done + 1, total)} מתוך {total}</>}
          {reading && (
            <>
              {' '}· <span className="text-foreground">{reading.filename}</span> — נקרא כבר{' '}
              <Ltr>{clock}</Ltr>, בדרך כלל 3–4 דקות לקובץ
            </>
          )}
          {!reading && !stalled && total > 0 && done < total && ' · ממתין לקובץ הבא'}
        </span>
        <span dir="ltr">{pct}%</span>
      </div>
      {/* The track breathes while a file is being read. A moving segment was tried
          and rejected: with one file it filled the whole bar and read as "done". */}
      <div className={`h-2 overflow-hidden rounded-full bg-muted ${reading && !stalled ? 'animate-pulse' : ''}`}>
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        אפשר לרענן את הדף או לסגור אותו — הקבצים כבר על השרת, והקריאה תימשך מהמקום שבו היא נמצאת.
      </p>
      {stalled && (
        <div className="mt-2 flex flex-wrap items-center gap-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
          <span>הקריאה נעצרה. מה שנקרא עד כה שמור — אפשר להמשיך מכאן.</span>
          <button
            type="button"
            onClick={onContinue}
            className="rounded-md bg-primary px-3 py-0.5 font-semibold text-primary-foreground"
          >
            המשך קריאה
          </button>
        </div>
      )}
    </div>
  );
}
